import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { KitchenSLASettings, Order } from '../../types';
import { getKdsTypography } from '../../lib/kdsTypography';
import {
  getEffectiveQuantityCompleted,
  getEffectiveQuantityCompletedForBatch,
  getEffectiveQuantityServedForBatch,
  getCardItemsWithTargets,
  getSeatsUsedByOrder,
} from '../../lib/orderUtils';
import ClockIcon from '../icons/ClockIcon';
import XCircleIcon from '../icons/XCircleIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import BuffetTimer from '../pos/BuffetTimer';

interface KitchenOrderCardProps {
  order: Order;
  /** When defined, show only this batch (0 = initial, >0 = running order update) */
  visibleBatchIndex?: number;
  onUpdateOrderItemStatus: (orderId: string, itemIndex: number, status: 'pending' | 'completed', quantityCompleted?: number, batchIndex?: number, targetQtyForCard?: number) => void;
  onStartOrder?: (orderId: string, batchIndex?: number) => void;
  onRemoveOrder?: (orderId: string) => Promise<void>;
  slaSettings?: Partial<KitchenSLASettings>;
  compactMode?: boolean;
  stickyMode?: boolean;
  /** Position in column for slight alternating tilt (sticky-note board). */
  noteLayoutIndex?: number;
  /** Unified kitchen board: show New vs Preparing as a tag (no separate columns). */
  listStage?: 'new' | 'preparing';
  /** Horizontal bump rail: drop outer margin so columns align cleanly. */
  railSlot?: boolean;
  /** Scale content to fit grid cell height (no internal scroll). */
  fitToCell?: boolean;
}

type KdsSlaTier = 'track' | 'medium' | 'critical' | 'urgent';

/** Urgent = start/serve SLA breach; Critical = elapsed past danger; Medium = past warning only. */
function computeKdsSla(
  now: number,
  order: Order,
  slaSettings: Partial<KitchenSLASettings> | undefined,
  showNewView: boolean
): { tier: KdsSlaTier; label: string } {
  const warn = slaSettings?.warningMinutes ?? 5;
  const danger = slaSettings?.dangerMinutes ?? Math.max(warn + 1, 10);
  const toStart = slaSettings?.minutesToStart ?? 5;
  const toServe = slaSettings?.minutesToServe ?? 5;
  const placedMinutes = Math.floor((now - order.timestamp) / 60000);
  const readyAt = order.readyAt;
  const readyMins = typeof readyAt === 'number' ? Math.floor((now - readyAt) / 60000) : 0;
  const startBreach = showNewView && placedMinutes >= toStart;
  const serveBreach = order.status === 'ready' && typeof readyAt === 'number' && readyMins >= toServe;
  if (startBreach || serveBreach) return { tier: 'urgent', label: 'Urgent' };
  if (placedMinutes >= danger) return { tier: 'critical', label: 'Critical' };
  if (placedMinutes >= warn) return { tier: 'medium', label: 'Medium' };
  return { tier: 'track', label: 'On track' };
}

function sanitizeKitchenNote(note: string | null | undefined): string {
  if (typeof note !== 'string') return '';
  let text = note.trim();
  if (!text) return '';
  const nonActionablePatterns = [
    /\bparty\s*of\s*\d+\b/gi,
    /\b\d+\s*(pax|guests?|covers?)\b/gi,
    /\b(pax|guests?|covers?)\s*[:=-]?\s*\d+\b/gi,
  ];
  nonActionablePatterns.forEach((pattern) => {
    text = text.replace(pattern, ' ');
  });
  text = text.replace(/[\s,;|/\-]{2,}/g, ' ').trim();
  text = text.replace(/^[,;|/\s\-]+|[,;|/\s\-]+$/g, '').trim();
  return text;
}

const KitchenOrderCard: React.FC<KitchenOrderCardProps> = ({
  order,
  visibleBatchIndex,
  onUpdateOrderItemStatus,
  onStartOrder,
  onRemoveOrder,
  slaSettings,
  compactMode = false,
  stickyMode = false,
  noteLayoutIndex = 0,
  listStage,
  railSlot = false,
  fitToCell = false,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  /** Chit header sits outside the scaled area so type stays readable when the item list scales down. */
  const chitHeaderRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);
  const [elapsedTime, setElapsedTime] = useState('00:00');
  const [slaTier, setSlaTier] = useState<KdsSlaTier>('track');
  const [slaLabel, setSlaLabel] = useState('On track');

  const isBuffet = order.orderType === 'buffet';

  const isDiscountItem = (item: Order['items'][0]) =>
    (typeof item.price === 'number' && item.price < 0) ||
    String(item.id).includes('combo-discount');

  // Always-available progress summary for this card scope.
  const itemProgress = useMemo(() => {
    const { items: cardItems } = getCardItemsWithTargets(order, visibleBatchIndex);
    const batchIdx = typeof visibleBatchIndex === 'number' ? visibleBatchIndex : 0;
    const total = cardItems.length;
    const completed = cardItems.filter(({ item, targetQty }) => {
      const doneQty = getEffectiveQuantityCompletedForBatch(item, batchIdx, targetQty);
      return doneQty >= targetQty;
    }).length;
    return { completed, total };
  }, [order, visibleBatchIndex]);

  const { removedItems, isNewByLineId, quantityDeltaByLineId, initialQuantityByKey } = useMemo(() => {
    const snapshot = order.initialKitchenSnapshot ?? order.lastSentItemSnapshot ?? [];
    const items = order.items ?? [];
    const itemsByLineId = new Map(
      items
        .filter((i) => !isDiscountItem(i))
        .map((i) => [i.lineId, i] as const)
        .filter(([lineId]) => !!lineId)
    );
    const removed: typeof snapshot = [];
    const isNew = new Map<string, boolean>();
    const quantityDelta = new Map<string, number>();
    const initialQty = new Map<string, number>();

    const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));

    // Detect removed and partially removed lines (reductions)
    snapshot.forEach((s) => {
      const current = itemsByLineId.get(s.lineId);
      if (!current || current.removed) {
        removed.push(s);
      } else if (typeof current.quantity === 'number' && current.quantity < s.quantity) {
        const delta = s.quantity - current.quantity;
        if (delta > 0) {
          removed.push({ ...s, quantity: delta });
        }
      }
    });

    items.forEach((item, idx) => {
      if (item.removed) return;
      const lineId = item.lineId ?? `idx-${idx}`;
      const prev = (item.lineId ? snapshotByLineId.get(item.lineId) : undefined) ?? snapshot[idx];
      const isNewLine = !prev;
      isNew.set(lineId, isNewLine);
      if (prev) {
        initialQty.set(lineId, prev.quantity);
        if (item.quantity > prev.quantity) {
          quantityDelta.set(lineId, item.quantity - prev.quantity);
        }
      }
    });

    return { removedItems: removed, isNewByLineId: isNew, quantityDeltaByLineId: quantityDelta, initialQuantityByKey: initialQty };
  }, [order.items, order.initialKitchenSnapshot, order.lastSentItemSnapshot]);

  /** For removed items, show original (snapshot) quantity sent to kitchen, not current item.quantity */
  const getRemovedItemQuantity = useCallback(
    (item: Order['items'][0]) =>
      (item.lineId ? removedItems.find((e) => e.lineId === item.lineId)?.quantity : undefined) ?? item.quantity,
    [removedItems]
  );

  /**
   * Main/base KDS ticket: only initial (batch 0) lines. Running additions have their own card —
   * never render orderBatches[1+] "Added …" sections here or lines duplicate on two tickets.
   */
  const batchesForBaseCardOnly = useMemo(() => {
    if (!order.orderBatches?.length) return null;
    const { items: cardItems } = getCardItemsWithTargets(order, undefined);
    if (cardItems.length === 0) return null;
    const batchItems = cardItems.map(({ item, originalIndex }) => ({ item, originalIndex }));
    return [{ label: 'Initial order', batchItems, batchIndex: 0 }];
  }, [order]);

  // For the base (initial) card, get stable target quantities from the shared helper.
  const baseCardTargetQtyByIndex = useMemo(() => {
    const map = new Map<number, number>();
    const { items } = getCardItemsWithTargets(order, undefined);
    items.forEach(({ originalIndex, targetQty }) => {
      map.set(originalIndex, targetQty);
    });
    return map;
  }, [order]);

  const isRunningOrderTicket = typeof visibleBatchIndex === 'number' && visibleBatchIndex > 0;
  const batchStarted = isRunningOrderTicket && visibleBatchIndex != null && order.preparingStartedByBatch?.[String(visibleBatchIndex)] != null;
  // Running-order cards use per-batch start state so each card can be started independently.
  const showNewView = isRunningOrderTicket ? !batchStarted : order.status === 'new';
  const showPreparingView = isRunningOrderTicket ? batchStarted : order.status === 'preparing';

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const diff = now - order.timestamp;
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setElapsedTime(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      const { tier, label } = computeKdsSla(now, order, slaSettings, showNewView);
      setSlaTier(tier);
      setSlaLabel(label);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [
    order.timestamp,
    order.status,
    order.readyAt,
    order.preparingStartedByBatch,
    slaSettings,
    showNewView,
    visibleBatchIndex,
  ]);

  // Running batch lines: must match getCardItemsWithTargets (initialKitchenSnapshot + itemIndices merge) or counts vs list disagree.
  const visibleBatchInfo = useMemo(() => {
    if (typeof visibleBatchIndex !== 'number' || visibleBatchIndex < 1) return null;
    const batches = order.orderBatches ?? [];
    const batch = batches[visibleBatchIndex];
    if (!batch) return null;
    const { items: cardItems } = getCardItemsWithTargets(order, visibleBatchIndex);
    if (cardItems.length === 0) return null;
    const batchItems = cardItems.map(({ item, originalIndex, targetQty, baselineQty }) => {
      const baseline = baselineQty ?? 0;
      return {
        item,
        originalIndex,
        displayQty: targetQty,
        baseline,
        batchTarget: baseline + targetQty,
      };
    });
    const label = `Added ${new Date(batch.timestamp).toLocaleTimeString('en-NZ', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    return { label, batchItems };
  }, [visibleBatchIndex, order.orderBatches, order.items, order.initialKitchenSnapshot, order.lastSentItemSnapshot]);

  const kdsTextSize = useMemo((): NonNullable<KitchenSLASettings['kitchenDisplayTextSize']> => {
    const s = slaSettings?.kitchenDisplayTextSize;
    if (!fitToCell) return s ?? 'large';
    /** Unified board: cap at `large` so rows fit; use Settings → standard for smaller type. */
    if (s === 'standard') return 'standard';
    if (s === 'large') return 'large';
    if (s === 'xlarge') return 'large';
    return 'large';
  }, [fitToCell, slaSettings?.kitchenDisplayTextSize]);

  const k = useMemo(() => getKdsTypography(kdsTextSize), [kdsTextSize]);
  const slaChipClass =
    slaTier === 'urgent'
      ? k.slaChipUrgent
      : slaTier === 'critical'
        ? k.slaChipCritical
        : slaTier === 'medium'
          ? k.slaChipMedium
          : k.slaChipTrack;
  /** Highlighter strip on top of each “sticky note” for SLA urgency. */
  const slaTopBar =
    slaTier === 'urgent'
      ? 'border-t-[7px] border-red-800 ring-2 ring-red-400/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]'
      : slaTier === 'critical'
        ? 'border-t-[7px] border-red-600'
        : slaTier === 'medium'
          ? 'border-t-[7px] border-amber-500'
          : 'border-t-[7px] border-emerald-600';
  const noteTilt = fitToCell
    ? ''
    : stickyMode
      ? '-rotate-[0.35deg]'
      : noteLayoutIndex % 2 === 0
        ? '-rotate-[0.65deg]'
        : 'rotate-[0.55deg]';
  const paperShell = stickyMode
    ? 'bg-gradient-to-br from-amber-100 via-[#fffef5] to-amber-50/95 border-amber-400/70 border-l-4 border-l-amber-500'
    : 'bg-gradient-to-br from-[#fffef9] via-[#fff6dc] to-[#f0e6c8] border-stone-500/35';
  const cardShellClass = fitToCell
    ? `rounded-md border border-stone-400/60 bg-[#e4e2ec] shadow-[0_4px_16px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden ${stickyMode ? 'shadow-[0_6px_22px_rgba(0,0,0,0.14)]' : ''}`
    : `rounded-sm border ${paperShell} ${slaTopBar} ${noteTilt} shadow-[0_12px_28px_rgba(0,0,0,0.14),0_1px_0_rgba(255,255,255,0.55)_inset] ${compactMode ? 'p-2 sm:p-2.5' : 'p-3 sm:p-4'} ${railSlot ? '' : compactMode ? 'mb-2.5' : 'mb-4'} ${stickyMode ? 'shadow-[0_14px_34px_rgba(100,75,15,0.2)]' : ''}`;

  const fitCellPad = fitToCell ? 'px-1.5 py-1' : 'px-2 py-1.5';
  const fitListUl = fitToCell ? 'space-y-1' : 'space-y-2';
  const fitHeadMb = fitToCell ? 'mb-1.5' : 'mb-2';
  const fitBlockTop = fitToCell ? 'mt-1.5 pt-1.5' : 'mt-2 pt-2';
  const fitBatchWrap = fitToCell ? 'mb-1.5 first:mt-0' : 'mb-3 first:mt-0';

  const orderTypeBadgeClass = useMemo(() => {
    switch (order.orderType) {
      case 'takeaway':
        return k.typeBadgeTakeaway;
      case 'online':
        return k.typeBadgeOnline;
      case 'uber-doordash':
        return k.typeBadgeUber;
      case 'delivery':
        return k.typeBadgeDelivery;
      case 'buffet':
        return k.typeBadgeBuffet;
      default:
        return k.typeBadgeDineIn;
    }
  }, [k, order.orderType]);

  const orderTypeBadge = useMemo(() => {
    if (order.orderType === 'takeaway') return 'TAKEAWAY';
    if (order.orderType === 'uber-doordash') return 'UBER/DOORDASH';
    if (order.orderType === 'delivery') {
      const platform = order.deliveryInfo?.platform?.toUpperCase();
      return platform || 'DELIVERY';
    }
    if (order.orderType === 'online') return 'ONLINE';
    if (order.orderType === 'buffet') return 'BUFFET';
    return 'DINE IN';
  }, [order.orderType, order.deliveryInfo?.platform]);

  /** Grid chit header: SLA drives colour (green → amber → red); order type stays in badges below. */
  const kdsSlaHeaderClass = useMemo(() => {
    switch (slaTier) {
      case 'urgent':
        return 'bg-gradient-to-br from-red-600 via-red-700 to-rose-900 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.18)]';
      case 'critical':
        return 'bg-gradient-to-br from-orange-600 via-red-600 to-red-800 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.14)]';
      case 'medium':
        return 'bg-gradient-to-br from-amber-500 via-orange-500 to-amber-700 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.12)]';
      default:
        return 'bg-gradient-to-br from-emerald-500 via-green-600 to-emerald-800 text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.1)]';
    }
  }, [slaTier]);

  const placedAt = useMemo(() => {
    const d = new Date(order.timestamp);
    return {
      ymd: d.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }),
      hm: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false }),
    };
  }, [order.timestamp]);

  const displayOrderNo = useMemo(() => {
    const digits = order.id.replace(/\D/g, '') || '0';
    const tail = digits.slice(-8).padStart(8, '0');
    return tail.slice(-6);
  }, [order.id]);

  const typePaxLine = useMemo(() => {
    const pax = getSeatsUsedByOrder(order);
    if (order.orderType === 'takeaway') return `TAKE AWAY · #${order.takeawayNumber ?? '?'}`;
    if (order.orderType === 'online') return 'ONLINE PICKUP';
    if (order.orderType === 'delivery' || order.orderType === 'uber-doordash') {
      return `${orderTypeBadge}${order.deliveryNumber != null ? ` · #${order.deliveryNumber}` : ''}`;
    }
    if (order.orderType === 'buffet') return `BUFFET · ${pax} Pax`;
    return `DINE IN · ${pax} Pax`;
  }, [order, orderTypeBadge]);
  const compactHeaderOrderType = useMemo(() => {
    if (order.orderType === 'dine-in') {
      const tableLabel = order.tableName || `Table ${order.tableNumber ?? order.tableId ?? '?'}`;
      return `DINE IN (${tableLabel})`;
    }
    if (order.orderType === 'takeaway') return 'TAKEAWAY';
    if (order.orderType === 'uber-doordash') return 'UBER/DOORDASH';
    if (order.orderType === 'delivery') return order.deliveryInfo?.platform?.toUpperCase() || 'DELIVERY';
    if (order.orderType === 'online') return 'DELIVERY';
    return orderTypeBadge;
  }, [order.orderType, order.tableId, order.tableName, order.tableNumber, order.deliveryInfo?.platform, orderTypeBadge]);
  const hasRunningOrderUpdates = useMemo(
    () =>
      Array.isArray(order.orderBatches) &&
      order.orderBatches.some((batch, idx) => idx > 0 && (batch.items?.length ?? 0) > 0),
    [order.orderBatches]
  );
  const compactHeaderOrderTypeDisplay = useMemo(() => {
    if (!isRunningOrderTicket && hasRunningOrderUpdates && order.orderType === 'dine-in') {
      return `${compactHeaderOrderType} · RUNNING ORDER`;
    }
    return compactHeaderOrderType;
  }, [compactHeaderOrderType, hasRunningOrderUpdates, isRunningOrderTicket, order.orderType]);
  const compactHeaderPrimaryLine = useMemo(() => {
    if (isRunningOrderTicket) {
      return `RUNNING · ${compactHeaderOrderType}`;
    }
    return compactHeaderOrderTypeDisplay;
  }, [isRunningOrderTicket, compactHeaderOrderType, compactHeaderOrderTypeDisplay]);
  const actionableOrderNote = useMemo(() => sanitizeKitchenNote(order.notes), [order.notes]);

  const chitTitleLine = useMemo(() => {
    if (isRunningOrderTicket) {
      return visibleBatchInfo ? `RUNNING ORDER #${(visibleBatchIndex ?? 1) + 1}` : 'RUNNING ORDER';
    }
    if (order.orderType === 'takeaway') return `TAKEAWAY #${order.takeawayNumber ?? '?'}`;
    if (order.orderType === 'online') return 'ONLINE ORDER';
    if (order.orderType === 'delivery' || order.orderType === 'uber-doordash') {
      return (order.deliveryInfo?.platform ?? 'DELIVERY').toUpperCase();
    }
    const t = order.tableName || `Table ${order.tableNumber ?? order.tableId ?? '?'}`;
    return `TABLE : ${t}`;
  }, [order, isRunningOrderTicket, visibleBatchInfo, visibleBatchIndex]);

  const itemRowTile = fitToCell ? 'rounded-sm border border-stone-300/85 bg-white shadow-sm' : 'odd:bg-gray-50 even:bg-white';

  function PrimaryItemBlock(props: {
    qty: number | string;
    name: React.ReactNode;
    isNew?: boolean;
    meat?: string | null;
    notes?: string | null;
    extraLine?: React.ReactNode;
  }) {
    const { qty, name, isNew, meat, notes, extraLine } = props;
    const actionableNotes = sanitizeKitchenNote(notes);
    if (!fitToCell) {
      return (
        <div className="flex-1 min-w-0 break-words">
          <span className="font-semibold">{qty}x</span> {name}
          {isNew && <span className={`ml-1 ${k.badgeNew}`}>ADDED</span>}
          {extraLine}
          {meat && <span className={`ml-2 ${k.meatOpt}`}>{meat}</span>}
          {actionableNotes && (
            <div className="mt-1.5">
              <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                <p className={`${k.noteLabel} text-yellow-900 mb-0.5 flex items-center`}>
                  <span className="mr-1">📝</span> Special Instructions:
                </p>
                <p className={`${k.noteBody} text-yellow-900`}>{actionableNotes}</p>
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="flex min-w-0 flex-1 flex-row items-start justify-between gap-1.5">
        <div className="min-w-0 flex-1 break-words text-left [overflow-wrap:anywhere]">
          <div className={`${k.itemRow} font-extrabold leading-tight text-stone-900`}>
            {name}
            {isNew && <span className={`ml-1 inline-block align-middle ${k.badgeNew}`}>ADDED</span>}
          </div>
          {extraLine ? <div className="mt-0.5">{extraLine}</div> : null}
          {meat && (
            <p className="mt-0.5 pl-0.5 text-sm font-semibold uppercase leading-snug tracking-wide text-stone-700">
              * {meat}
            </p>
          )}
          {actionableNotes && (
            <div className="mt-1 rounded-md border-2 border-amber-500 bg-amber-50 px-1.5 py-1 shadow-md ring-2 ring-amber-400/45">
              <p className={`${k.noteLabel} flex items-center gap-1 text-amber-950`}>
                <span aria-hidden>📝</span>
                Special instructions
              </p>
              <p className={`${k.noteBody} mt-0.5 whitespace-pre-wrap leading-snug text-amber-950`}>{actionableNotes}</p>
            </div>
          )}
        </div>
        <span
          className={`shrink-0 self-start font-black tabular-nums text-stone-900 ${fitToCell ? 'text-lg leading-tight sm:text-xl' : ''}`}
        >
          {qty}x
        </span>
      </div>
    );
  }

  const listSectionClass = fitToCell
    ? 'flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-y-auto overflow-x-hidden px-1 py-0.5 sm:px-1.5 sm:py-1'
    : `${fitBlockTop} border-t border-gray-100 pr-1`;

  const doneBtnChit = fitToCell ? 'shrink-0 min-h-9 rounded-md px-2 py-1.5 text-xs font-extrabold shadow-sm' : '';

  let orderIdentifier: React.ReactNode;
  let pickupTimeLabel: React.ReactNode = null;

  const pickupRaw: any =
    (order as any).pickupTime ??
    (order as any).pickup_time ??
    (order as any).pickupAt ??
    (order as any).pickup_at ??
    (order as any).pickupTimestamp ??
    (order as any).pickup_timestamp;

  if (typeof pickupRaw === 'number' && !Number.isNaN(pickupRaw)) {
    const raw = pickupRaw;
    const pickupTimeMs = raw < 1_000_000_000_000 ? raw * 1000 : raw;
    const dt = new Date(pickupTimeMs);
    const formatted = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    pickupTimeLabel = (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300 ml-2">
        Pickup {formatted}
      </span>
    );
  } else if (pickupRaw && typeof pickupRaw === 'object') {
    // Firestore Timestamp or similar
    if (typeof pickupRaw.toMillis === 'function') {
      const ms = pickupRaw.toMillis();
      if (typeof ms === 'number' && !Number.isNaN(ms)) {
        const dt = new Date(ms);
        const formatted = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        pickupTimeLabel = (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300 ml-2">
            Pickup {formatted}
          </span>
        );
      }
    } else if (typeof pickupRaw.seconds === 'number') {
      const ms = pickupRaw.seconds * 1000;
      const dt = new Date(ms);
      const formatted = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      pickupTimeLabel = (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300 ml-2">
          Pickup {formatted}
        </span>
      );
    }
  }

  if (order.orderType === 'takeaway') {
    orderIdentifier = (
      <span>
        {`TAKEOUT-${order.takeawayNumber}`}
        {pickupTimeLabel}
      </span>
    );
  } else if (order.orderType === 'online') {
    orderIdentifier = (
      <span>
        ONLINE ORDER
        {pickupTimeLabel}
      </span>
    );
  } else if (order.orderType === 'buffet') {
    const buffetTypeLabel = order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet';
    orderIdentifier = (
      <div>
        <div className="flex items-center gap-2">
          <span>{buffetTypeLabel} BUFFET - <span className={k.tableName}>{order.tableName || `Table ${order.tableNumber || order.tableId}`}</span></span>
          <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
            BUFFET
          </span>
        </div>
        {order.customerName && (
          <p className={k.buffetSubtext}>{order.customerName}</p>
        )}
        {!order.customerName && order.customerNumber && (
          <p className={k.buffetSubtext}>Customer {order.customerNumber}</p>
        )}
      </div>
    );
  } else if (order.orderType === 'uber-doordash') {
    orderIdentifier = (
      <span>
        {order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash'}
        {pickupTimeLabel}
      </span>
    );
  } else if (order.orderType === 'delivery') {
    const deliveryNum = `DELIVERY-${order.deliveryNumber}`;
    if (order.deliveryInfo) {
      orderIdentifier = (
        <span>
          {order.deliveryInfo.platform}
          {pickupTimeLabel}
        </span>
      );
      const subtext = `${deliveryNum} - ${order.deliveryInfo.customerName}`;
      return (
        <div className={cardShellClass}>
          <div className={`flex justify-between items-center ${fitHeadMb}`}>
            <div className="flex-1 min-w-0">
              <h4 className={k.cardTitle}>{orderIdentifier}</h4>
              <p className={k.deliverySubtext}>{subtext}</p>
              <div className="mt-1">
                <span className={orderTypeBadgeClass}>DELIVERY</span>
              </div>
            </div>
            <div className="ml-2 flex shrink-0 items-center gap-2">
              {order.status === 'new' && onStartOrder && (
                <button
                  type="button"
                  onClick={() => onStartOrder(order.id)}
                  className="shrink-0 rounded-md bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm ring-1 ring-blue-800/30 hover:bg-blue-700 active:scale-[0.97]"
                >
                  Start
                </button>
              )}
              <div className="flex flex-col items-end">
                <span className={k.progressBig}>
                  {itemProgress.completed}/{itemProgress.total}
                </span>
                <span className={k.progressItemsLabel}>items</span>
                <span className={`mt-1 ${k.timer}`}>
                  <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> {elapsedTime}
                </span>
                <span className={`mt-1 ${slaChipClass}`}>{slaLabel}</span>
              </div>
            </div>
          </div>
          {/* Show item list for new orders (read-only, no buttons) */}
          {order.status === 'new' && (
            <ul className="mt-2 pt-2 border-t border-gray-100 space-y-2 pr-1">
              {removedItems.length > 0 && removedItems.map((entry) => (
                <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                  <span className="font-semibold">{entry.quantity}x</span>
                  <span className="line-through">{entry.name}</span>
                  {entry.selectedMeatOption && <span className={k.meatOptParen}>({entry.selectedMeatOption})</span>}
                  <span className={k.removedTag}>REMOVED</span>
                </li>
              ))}
              {order.items.map((item, index) => {
                const isDiscount = item.price < 0 || item.id.toString().includes('combo-discount');
                if (isDiscount) return null;
                const isNew = item.lineId ? isNewByLineId.get(item.lineId) : false;

                return (
                  <li 
                    key={item.lineId ?? `${item.id}-${index}`} 
                    className={`${k.itemRow} flex items-start justify-between gap-3 text-gray-700 rounded-md ${fitCellPad} ${itemRowTile}`}
                  >
                    <div className="flex-1 min-w-0 break-words">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{item.quantity}x</span> {item.name} 
                        {isNew && (
                          <span className={k.badgeNew}>ADDED</span>
                        )}
                        {item.isRemake && (
                          <span className={k.badgeRemake}>
                            🔄 REMAKE
                          </span>
                        )}
                      </div>
                      {item.isRemake && item.remakeReason && (
                        <p className={k.remakeReason}>Reason: {item.remakeReason}</p>
                      )}
                      {item.selectedMeatOption && (
                        <span className={`ml-2 ${k.meatOpt}`}>
                          {item.selectedMeatOption}
                        </span>
                      )}
                      {sanitizeKitchenNote(item.notes) && (
                        <div className="mt-1.5">
                          <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                            <p className={`${k.noteLabel} text-yellow-900 mb-0.5 flex items-center`}>
                              <span className="mr-1">📝</span>
                              Special Instructions:
                            </p>
                            <p className={`${k.noteBody} text-yellow-900`}>{sanitizeKitchenNote(item.notes)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Show item list with Done buttons for preparing orders */}
          {order.status === 'preparing' && (
            <ul className="mt-2 pt-2 border-t border-gray-100 space-y-2 pr-1">
              {removedItems.length > 0 && removedItems.map((entry) => (
                <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                  <span className="font-semibold">{entry.quantity}x</span>
                  <span className="line-through">{entry.name}</span>
                          <span className={k.removedTag}>REMOVED</span>
                </li>
              ))}
              {order.items.map((item, index) => {
                const isDiscount = item.price < 0 || item.id.toString().includes('combo-discount');
                if (isDiscount) return null;
                if (item.removed) {
                  return (
                    <li key={item.lineId ?? `removed-${index}`} className={k.removedItem}>
                      <span className="font-semibold">{getRemovedItemQuantity(item)}x</span>
                      <span className="line-through">{item.name}</span>
                      <span className={k.removedTag}>REMOVED</span>
                    </li>
                  );
                }
                const isCompleted = item.status === 'completed';
                const isServed = Boolean(item.served);
                const canToggle = !isServed;
                const isNew = item.lineId ? isNewByLineId.get(item.lineId) : false;

                return (
                  <li 
                    key={item.lineId ?? `${item.id}-${index}`} 
                    className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}
                  >
                    <div className="flex-1 min-w-0 break-words">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{item.quantity}x</span> {item.name} 
                        {isNew && (
                          <span className={k.badgeNew}>ADDED</span>
                        )}
                        {item.isRemake && (
                          <span className={k.badgeRemake}>
                            🔄 REMAKE
                          </span>
                        )}
                      </div>
                      {item.isRemake && item.remakeReason && (
                        <p className={k.remakeReason}>Reason: {item.remakeReason}</p>
                      )}
                      {item.selectedMeatOption && (
                        <span className={`ml-2 ${k.meatOpt}`}>
                          {item.selectedMeatOption}
                        </span>
                      )}
                      {sanitizeKitchenNote(item.notes) && (
                        <div className="mt-1.5">
                          <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                            <p className={`${k.noteLabel} text-yellow-900 mb-0.5 flex items-center`}>
                              <span className="mr-1">📝</span>
                              Special Instructions:
                            </p>
                            <p className={`${k.noteBody} text-yellow-900`}>{sanitizeKitchenNote(item.notes)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={!canToggle}
                      onClick={() => {
                        if (!canToggle) return;
                        onUpdateOrderItemStatus(order.id, index, isCompleted ? 'pending' : 'completed');
                      }}
                      className={`${k.doneBtn} ${
                        canToggle
                          ? (isCompleted
                              ? 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                              : 'bg-green-500 text-white hover:bg-green-600')
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isCompleted ? 'Undo' : 'Done'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Show item list with Undo for awaiting service (delivery branch) */}
          {order.status === 'ready' && (() => {
            const preparableItems = order.items.filter(item => !(item.price < 0 || item.id.toString().includes('combo-discount')) && !item.removed);
            const allCompleted = preparableItems.length > 0 && preparableItems.every(item => item.status === 'completed');
            const allServed = preparableItems.length > 0 && preparableItems.every(item => item.served === true);
            return allCompleted && !allServed;
          })() && (
            <ul className="mt-2 pt-2 border-t border-gray-100 space-y-2 pr-1">
              {removedItems.length > 0 && removedItems.map((entry) => (
                <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                  <span className="font-semibold">{entry.quantity}x</span>
                  <span className="line-through">{entry.name}</span>
                          <span className={k.removedTag}>REMOVED</span>
                </li>
              ))}
              {order.items.map((item, index) => {
                const isDiscount = item.price < 0 || item.id.toString().includes('combo-discount');
                if (isDiscount) return null;
                if (item.removed) {
                  return (
                    <li key={item.lineId ?? `removed-${index}`} className={k.removedItem}>
                      <span className="font-semibold">{getRemovedItemQuantity(item)}x</span>
                      <span className="line-through">{item.name}</span>
                      <span className={k.removedTag}>REMOVED</span>
                    </li>
                  );
                }
                const keyForLine = item.lineId ?? `idx-${index}`;
                const initialQty = initialQuantityByKey.get(keyForLine) ?? item.quantity;
                const effectiveCompleted = getEffectiveQuantityCompleted(item);
                const isCompleted = effectiveCompleted >= initialQty;
                const effectiveServed = getEffectiveQuantityServedForBatch(item, 0, initialQty);
                const canToggle = effectiveServed < initialQty;
                const lineFullyServed = effectiveServed >= initialQty;
                const isNew = item.lineId ? isNewByLineId.get(item.lineId) : false;

                return (
                  <li 
                    key={item.lineId ?? `${item.id}-${index}`} 
                    className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}
                  >
                    <div className="flex-1 min-w-0 break-words">
                      <span className="font-semibold">{item.quantity}x</span> {item.name} 
                      {isNew && (
                        <span className={`ml-1 ${k.badgeNew}`}>ADDED</span>
                      )}
                      {item.selectedMeatOption && (
                        <span className={`ml-2 ${k.meatOpt}`}>
                          {item.selectedMeatOption}
                        </span>
                      )}
                      {sanitizeKitchenNote(item.notes) && (
                        <div className="mt-1.5">
                          <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                            <p className={`${k.noteLabel} mb-0.5 flex items-center`}>
                              <span className="mr-1">📝</span>
                              Special Instructions:
                            </p>
                            <p className={k.noteBody}>{sanitizeKitchenNote(item.notes)}</p>
                          </div>
                        </div>
                      )}
                      <div className="mt-1">
                        {lineFullyServed ? (
                          <span className={k.awaitingStatus}>
                            <CheckCircleIcon className="w-3 h-3" /> Served
                          </span>
                        ) : (
                          <span className={k.awaitingAmber}>
                            <ClockIcon className="w-3 h-3" /> Awaiting Service
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={!canToggle}
                      onClick={() => {
                        if (!canToggle) return;
                        const newCompleted = isCompleted ? 0 : initialQty;
                        onUpdateOrderItemStatus(order.id, index, newCompleted >= item.quantity ? 'completed' : 'pending', newCompleted);
                      }}
                      className={`${k.doneBtn} ${
                        canToggle
                          ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-green-500 text-white hover:bg-green-600')
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isCompleted ? 'Undo' : 'Done'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {actionableOrderNote && (
            <div className="mt-2 pt-2 border-t border-yellow-200 bg-yellow-50 rounded p-2 ring-1 ring-amber-600/25">
              <p className={k.orderNotesTitle}>Order Notes:</p>
              <p className={k.orderNotesBody}>{actionableOrderNote}</p>
            </div>
          )}
          {onRemoveOrder && (
            <button
              onClick={async () => {
                if (confirm('Remove this order from kitchen display?')) {
                  await onRemoveOrder(order.id);
                }
              }}
              className={k.removeBtn}
            >
              <XCircleIcon className="w-4 h-4" />
              Remove Order
            </button>
          )}
        </div>
      );
    } else {
      orderIdentifier = deliveryNum;
    }
  } else {
    const tableName = order.tableName || `Table ${order.tableNumber ?? order.tableId ?? '?'}`;
    // KDS: table only (no Customer 1/2 — avoids party/counter clutter; same focus as QSR bump screens).
    orderIdentifier = (
      <span className={k.tableName}>
        {tableName}
        {hasRunningOrderUpdates ? ' · RUNNING ORDER' : ''}
      </span>
    );
  }

  const filteredItems = order.items.filter(item => !(item.price < 0 || item.id.toString().includes('combo-discount')));
  
  // Check if all items are completed (ready for service)
  const allItemsCompleted = filteredItems.length > 0 && filteredItems.every(item => item.status === 'completed');
  const allItemsServed = filteredItems.length > 0 && filteredItems.every(item => item.served === true);

  useEffect(() => {
    // Keep fit-mode cards at stable readable scale. List area handles overflow via scrolling.
    setFitScale(1);
  }, [fitToCell]);

  // For running-order tickets, compute just the "delta" items to show (match by lineId or index)
  const deltaDisplayItems = useMemo(() => {
    if (!isRunningOrderTicket) return [] as { item: Order['items'][0]; originalIndex: number; displayQty: number; isNew: boolean }[];
    const snapshot = order.initialKitchenSnapshot ?? order.lastSentItemSnapshot ?? [];
    const items = order.items ?? [];
    const result: { item: Order['items'][0]; originalIndex: number; displayQty: number; isNew: boolean }[] = [];
    items.forEach((item, idx) => {
      if (isDiscountItem(item) || item.removed) return;
      const key = item.lineId ?? `idx-${idx}`;
      const isNew = isNewByLineId.get(key) ?? false;
      let deltaQty = quantityDeltaByLineId.get(key) ?? 0;
      // Fallback: if no delta in map but snapshot has lower qty at same index (e.g. snapshot used pos-X keys), compute delta
      if (deltaQty === 0 && !isNew && snapshot[idx] && item.quantity > (snapshot[idx]?.quantity ?? 0)) {
        deltaQty = item.quantity - (snapshot[idx].quantity ?? 0);
      }
      if (isNew) {
        result.push({ item, originalIndex: idx, displayQty: item.quantity, isNew: true });
      } else if (deltaQty > 0) {
        result.push({ item: { ...item, quantity: deltaQty }, originalIndex: idx, displayQty: deltaQty, isNew: false });
      }
    });
    return result;
  }, [isRunningOrderTicket, order.items, order.initialKitchenSnapshot, order.lastSentItemSnapshot, isNewByLineId, quantityDeltaByLineId]);

  return (
    <div
      className={`${cardShellClass} ${allItemsCompleted && !allItemsServed ? 'ring-2 ring-green-300' : ''} ${
        fitToCell ? 'flex h-full min-h-0 flex-col overflow-hidden' : ''
      }`}
    >
      <div ref={cellRef} className={fitToCell ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : ''}>
        {fitToCell && (
          <div ref={chitHeaderRef} className={`${kdsSlaHeaderClass} shrink-0 px-3 py-2 shadow-[0_2px_8px_rgba(0,0,0,0.2)]`}>
            <div className="flex items-center justify-between gap-3">
              <p className="min-w-0 flex-1 truncate text-sm font-black uppercase tracking-wide text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.45)] sm:text-base">
                {compactHeaderPrimaryLine}
              </p>
              <p className="shrink-0 text-xs font-semibold tabular-nums text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]">
                {placedAt.hm}
              </p>
            </div>
            <div className="mt-1.5 grid grid-cols-3 gap-1.5 text-[11px] text-white sm:text-xs">
              <span className="rounded-md bg-black/20 px-2 py-1 font-bold">
                Order no. {displayOrderNo}
              </span>
              <span className="rounded-md bg-black/20 px-2 py-1 text-center font-bold">
                {itemProgress.completed}/{itemProgress.total} items
              </span>
              <span className="inline-flex items-center justify-center gap-1 rounded-md bg-black/20 px-2 py-1 font-semibold">
                <ClockIcon className="h-3.5 w-3.5 shrink-0 opacity-95" /> {elapsedTime}
              </span>
            </div>
            {showNewView && onStartOrder && (
              <button
                type="button"
                onClick={() => onStartOrder(order.id, visibleBatchIndex)}
                className={`mt-1.5 flex w-full items-center justify-center rounded-md bg-blue-600 font-extrabold uppercase tracking-wide text-white shadow-md ring-2 ring-blue-900/30 hover:bg-blue-500 active:bg-blue-700 ${
                  fitToCell ? 'min-h-8 py-1.5 text-xs' : 'min-h-11 py-2.5 text-sm'
                }`}
              >
                Start order
              </button>
            )}
          </div>
        )}
        <div
          ref={innerRef}
          className={
            fitToCell
              ? 'flex min-h-0 min-w-0 flex-1 basis-0 flex-col overflow-visible origin-top-left will-change-transform'
              : 'w-full'
          }
          style={
            fitToCell
              ? {
                  transform: `scale(${fitScale})`,
                  width: `${100 / Math.max(fitScale, 0.001)}%`,
                  transformOrigin: 'top left' as const,
                }
              : undefined
          }
        >
      {!fitToCell ? (
        <div className={`flex justify-between items-center ${fitHeadMb}`}>
          <div className="flex-1 min-w-0">
            {isRunningOrderTicket ? (
              <>
                <h4 className={k.runningTitle}>
                  {visibleBatchInfo ? `RUNNING ORDER #${(visibleBatchIndex ?? 1) + 1}` : 'RUNNING ORDER'}
                </h4>
                <div className="mt-0.5">
                  <div className={k.runningSub}>
                    {orderIdentifier}
                  </div>
                  {visibleBatchInfo && (
                    <div className={k.batchTimeLine}>{visibleBatchInfo.label}</div>
                  )}
                </div>
              </>
            ) : (
              <h4 className={k.cardTitle}>{orderIdentifier}</h4>
            )}
            {allItemsCompleted && !allItemsServed && (
              <p className={k.readyForServiceLine}>✓ Ready for Service</p>
            )}
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {listStage === 'new' && (
                <span className="inline-flex items-center rounded-sm bg-rose-600 px-1.5 py-0.5 text-[10px] sm:text-xs font-extrabold uppercase tracking-wide text-white shadow-sm ring-1 ring-rose-800/40">
                  New
                </span>
              )}
              {listStage === 'preparing' && (
                <span className="inline-flex items-center rounded-sm bg-amber-600 px-1.5 py-0.5 text-[10px] sm:text-xs font-extrabold uppercase tracking-wide text-white shadow-sm ring-1 ring-amber-900/30">
                  Preparing
                </span>
              )}
              <span className={orderTypeBadgeClass}>
                {orderTypeBadge}
              </span>
              <span className={k.progressBadge}>
                Order items {itemProgress.completed}/{itemProgress.total}
              </span>
            </div>
          </div>
          <div className="ml-2 flex shrink-0 items-center gap-2">
            {showNewView && onStartOrder && (
              <button
                type="button"
                onClick={() => onStartOrder(order.id, visibleBatchIndex)}
                className="shrink-0 rounded-md bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm ring-1 ring-blue-800/30 hover:bg-blue-700 active:scale-[0.97]"
              >
                Start
              </button>
            )}
            <div className="flex flex-col items-end">
              <span className={k.progressBig}>
                {itemProgress.completed}/{itemProgress.total}
              </span>
              <span className={k.progressItemsLabel}>items</span>
              <span className={`mt-1 ${k.timer}`}>
                <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> {elapsedTime}
              </span>
              <span className={`mt-1 ${slaChipClass}`}>{slaLabel}</span>
            </div>
          </div>
        </div>
      ) : null}
      {/* Show item list for new orders (read-only, no buttons) */}
          {showNewView && (
        <div className={listSectionClass}>
          {isRunningOrderTicket ? (
            <ul className={fitListUl}>
              {removedItems.length > 0 &&
                removedItems.map((entry) => (
                  <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                    <span className="font-semibold">{entry.quantity}x</span>
                    <span className="line-through">{entry.name}</span>
                    {entry.selectedMeatOption && <span className={k.meatOptParen}>({entry.selectedMeatOption})</span>}
                    <span className={k.removedTag}>REMOVED</span>
                  </li>
                ))}
              {(visibleBatchInfo ? visibleBatchInfo.batchItems.length : deltaDisplayItems.length) > 0 ? (
                (visibleBatchInfo
                  ? visibleBatchInfo.batchItems.map(({ item, originalIndex, displayQty }) => ({
                      item,
                      originalIndex,
                      displayQty,
                      isNew: false,
                    }))
                  : deltaDisplayItems
                ).map(({ item, originalIndex, displayQty, isNew }) => (
                  <li
                    key={item.lineId ?? `${item.id}-${originalIndex}`}
                    className={`${k.itemRow} flex items-start justify-between gap-3 text-gray-700 rounded-md ${fitCellPad} ${itemRowTile}`}
                  >
                    <PrimaryItemBlock
                      qty={displayQty}
                      name={item.name}
                      isNew={isNew}
                      meat={item.selectedMeatOption ?? null}
                      notes={sanitizeKitchenNote(item.notes)}
                    />
                  </li>
                ))
              ) : removedItems.length === 0 ? (
                <li className={k.emptyLine}>No items to add or remove.</li>
              ) : null}
            </ul>
          ) : batchesForBaseCardOnly ? (
            batchesForBaseCardOnly.map((batch) => (
              <div key={`batch-${batch.batchIndex}-${batch.label}`} className={fitBatchWrap}>
                <p className={k.batchSectionLabel}>{batch.label}</p>
                <ul className={`${fitListUl} mt-1`}>
                  {batch.batchItems.map(({ item, originalIndex }) => {
                    if (item.removed) {
                      return (
                        <li key={item.lineId ?? `removed-${originalIndex}`} className={k.removedItem}>
                          <span className="font-semibold">{getRemovedItemQuantity(item)}x</span>
                          <span className="line-through">{item.name}</span>
                          {item.selectedMeatOption && <span className={k.meatOptParen}>({item.selectedMeatOption})</span>}
                          <span className={k.removedTag}>REMOVED</span>
                        </li>
                      );
                    }
                    const keyForLine = item.lineId ?? `idx-${originalIndex}`;
                    const originalQty = initialQuantityByKey.get(keyForLine);
                    // For the initial card (batchIndex === 0), show targetQty from baseCardTargetQtyByIndex.
                    // For later batches, fall back to current quantity.
                    const baseTargetQty = batch.batchIndex === 0 ? baseCardTargetQtyByIndex.get(originalIndex) : undefined;
                    const displayQty = baseTargetQty != null ? baseTargetQty : item.quantity;
                    const removedDelta =
                      originalQty != null && originalQty > item.quantity ? originalQty - item.quantity : 0;
                    const isNew = item.lineId ? isNewByLineId.get(item.lineId) : false;
                    return (
                      <li key={item.lineId ?? `${item.id}-${originalIndex}`} className={`${k.itemRow} flex items-start justify-between gap-3 text-gray-700 rounded-md ${fitCellPad} ${itemRowTile}`}>
                        <PrimaryItemBlock
                          qty={displayQty}
                          name={item.name}
                          isNew={isNew}
                          meat={item.selectedMeatOption ?? null}
                          notes={sanitizeKitchenNote(item.notes)}
                          extraLine={
                            removedDelta > 0 ? (
                              <span className={k.deltaHint}>
                                -{removedDelta} REMOVED (was {originalQty})
                              </span>
                            ) : null
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          ) : (
            <ul className={fitListUl}>
              {removedItems.length > 0 && removedItems.map((entry) => (
                <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                  <span className="font-semibold">{entry.quantity}x</span>
                  <span className="line-through">{entry.name}</span>
                  {entry.selectedMeatOption && <span className={k.meatOptParen}>({entry.selectedMeatOption})</span>}
                  <span className={k.removedTag}>REMOVED</span>
                </li>
              ))}
              {filteredItems.map((item, index) => {
                const originalIndex = order.items.findIndex(i => (i.lineId ? i.lineId === item.lineId : i.id === item.id));
                if (originalIndex === -1) return null;
                if (item.removed) {
                  return (
                    <li key={item.lineId ?? `removed-${originalIndex}`} className={k.removedItem}>
                      <span className="font-semibold">{item.quantity}x</span>
                      <span className="line-through">{item.name}</span>
                      {item.selectedMeatOption && <span className={k.meatOptParen}>({item.selectedMeatOption})</span>}
                      <span className={k.removedTag}>REMOVED</span>
                    </li>
                  );
                }
                const isNew = item.lineId ? isNewByLineId.get(item.lineId) : false;
                return (
                  <li key={item.lineId ?? `${item.id}-${originalIndex}`} className={`${k.itemRow} flex items-start justify-between gap-3 text-gray-700 rounded-md ${fitCellPad} ${itemRowTile}`}>
                    <PrimaryItemBlock
                      qty={item.quantity}
                      name={item.name}
                      isNew={!!isNew}
                      meat={item.selectedMeatOption ?? null}
                      notes={sanitizeKitchenNote(item.notes)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Show item list with Done buttons for preparing orders */}
          {showPreparingView && (
        <div className={listSectionClass}>
          {isRunningOrderTicket ? (
            <ul className={fitListUl}>
              {removedItems.length > 0 &&
                removedItems.map((entry) => (
                  <li key={`removed-prep-${entry.lineId}`} className={k.removedItem}>
                    <span className="font-semibold">{entry.quantity}x</span>
                    <span className="line-through">{entry.name}</span>
                    {entry.selectedMeatOption && <span className={k.meatOptParen}>({entry.selectedMeatOption})</span>}
                    <span className={k.removedTag}>REMOVED</span>
                  </li>
                ))}
              {(visibleBatchInfo ? visibleBatchInfo.batchItems : deltaDisplayItems).length > 0 ? (
                (visibleBatchInfo
                  ? visibleBatchInfo.batchItems.map(({ item, originalIndex, displayQty, baseline, batchTarget }) => ({
                      fullItem: item,
                      originalIndex,
                      displayQty,
                      isNewItem: false,
                      baseline,
                      batchTarget,
                    }))
                  : deltaDisplayItems.map(({ item, originalIndex, displayQty, isNew }) => {
                      const base = initialQuantityByKey.get(item.lineId ?? `idx-${originalIndex}`) ?? 0;
                      return {
                        fullItem: order.items?.[originalIndex] ?? item,
                        originalIndex,
                        displayQty,
                        isNewItem: isNew,
                        baseline: base,
                        batchTarget: base + displayQty,
                      };
                    })
                ).map(({ fullItem, originalIndex, displayQty, isNewItem, baseline, batchTarget }) => {
                  const batchIdx = typeof visibleBatchIndex === 'number' ? visibleBatchIndex : 1;
                  const effectiveCompleted = getEffectiveQuantityCompletedForBatch(fullItem, batchIdx, displayQty);
                  const isCompleted = effectiveCompleted >= displayQty;
                  // For running cards: use per-batch served so serving one card doesn't disable Done on the other
                  const effectiveServed = getEffectiveQuantityServedForBatch(fullItem, batchIdx, displayQty);
                  const canToggle = effectiveServed < displayQty;
                  return (
                    <li
                      key={`${fullItem.lineId ?? fullItem.id}-${originalIndex}-${visibleBatchIndex ?? 0}`}
                      className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}
                    >
                      <PrimaryItemBlock
                        qty={displayQty}
                        name={fullItem.name}
                        isNew={!!isNewItem}
                        meat={fullItem.selectedMeatOption ?? null}
                        notes={fullItem.notes ?? null}
                      />
                      <button
                        type="button"
                        disabled={!canToggle}
                        onClick={() => {
                          if (!canToggle) return;
                          const batchQty = isCompleted ? 0 : displayQty;
                          onUpdateOrderItemStatus(order.id, originalIndex, batchQty >= displayQty ? 'completed' : 'pending', batchQty, batchIdx, displayQty);
                        }}
                        className={`${k.doneBtn} ${doneBtnChit} ${
                          canToggle
                            ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-emerald-600 text-white hover:bg-emerald-700')
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {isCompleted ? 'Undo' : 'Done'}
                      </button>
                    </li>
                  );
                })
              ) : removedItems.length === 0 ? (
                <li className={k.emptyLine}>No items to add or remove.</li>
              ) : null}
            </ul>
          ) : batchesForBaseCardOnly ? (
            batchesForBaseCardOnly.map((batch) => (
              <div key={`batch-${batch.batchIndex}-${batch.label}`} className={fitBatchWrap}>
                <p className={k.batchSectionLabel}>{batch.label}</p>
                <ul className={`${fitListUl} mt-1`}>
                  {batch.batchItems.map(({ item, originalIndex }) => {
                    if (item.removed) {
                      return (
                        <li key={item.lineId ?? `removed-${originalIndex}`} className={k.removedItem}>
                          <span className="font-semibold">{getRemovedItemQuantity(item)}x</span>
                          <span className="line-through">{item.name}</span>
                          <span className={k.removedTag}>REMOVED</span>
                        </li>
                      );
                    }
                    const keyForLine = item.lineId ?? `idx-${originalIndex}`;
                    const initialQty = initialQuantityByKey.get(keyForLine) ?? item.quantity;
                    const effectiveCompleted = getEffectiveQuantityCompletedForBatch(item, 0, initialQty);
                    const isCompleted = effectiveCompleted >= initialQty;
                    const effectiveServed = getEffectiveQuantityServedForBatch(item, 0, initialQty);
                    const canToggle = effectiveServed < initialQty;
                    const isNew = isNewByLineId.get(keyForLine) ?? false;
                    const removedDeltaPrep = initialQty > item.quantity ? initialQty - item.quantity : 0;
                    const displayQtyPrep = batch.batchIndex === 0 ? (baseCardTargetQtyByIndex.get(originalIndex) ?? initialQty) : item.quantity;
                    return (
                      <li
                        key={item.lineId ?? `${item.id}-${originalIndex}`}
                        className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}
                      >
                        <PrimaryItemBlock
                          qty={displayQtyPrep}
                          name={item.name}
                          isNew={!!isNew}
                          meat={item.selectedMeatOption ?? null}
                          notes={sanitizeKitchenNote(item.notes)}
                          extraLine={
                            removedDeltaPrep > 0 ? (
                              <span className={k.deltaHint}>
                                -{removedDeltaPrep} REMOVED (was {initialQty})
                              </span>
                            ) : null
                          }
                        />
                        <button
                          type="button"
                          disabled={!canToggle}
                          onClick={() => {
                            if (!canToggle) return;
                            const batchQty = isCompleted ? 0 : initialQty;
                            onUpdateOrderItemStatus(order.id, originalIndex, batchQty > 0 ? 'completed' : 'pending', batchQty, 0, initialQty);
                          }}
                          className={`${k.doneBtn} ${doneBtnChit} ${
                            canToggle
                              ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-emerald-600 text-white hover:bg-emerald-700')
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isCompleted ? 'Undo' : 'Done'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          ) : (
            <ul className={fitListUl}>
              {removedItems.length > 0 && removedItems.map((entry) => (
                <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                  <span className="font-semibold">{entry.quantity}x</span>
                  <span className="line-through">{entry.name}</span>
                          <span className={k.removedTag}>REMOVED</span>
                </li>
              ))}
              {filteredItems.map((item, index) => {
                const originalIndex = order.items.findIndex(i => (i.lineId ? i.lineId === item.lineId : i.id === item.id));
                if (originalIndex === -1) return null;
                const keyForLine = item.lineId ?? `idx-${originalIndex}`;
                const initialQty = initialQuantityByKey.get(keyForLine) ?? item.quantity;
                const effectiveCompleted = getEffectiveQuantityCompleted(item);
                const isCompleted = effectiveCompleted >= initialQty;
                const isServed = Boolean(item.served);
                const canToggle = !isServed;
                const isNew = isNewByLineId.get(keyForLine) ?? false;
                const removedDeltaPrep2 = initialQty > item.quantity ? initialQty - item.quantity : 0;
                return (
                  <li
                    key={item.lineId ?? `${item.id}-${originalIndex}`}
                    className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}
                  >
                    <PrimaryItemBlock
                      qty={item.quantity}
                      name={item.name}
                      isNew={!!isNew}
                      meat={item.selectedMeatOption ?? null}
                      notes={sanitizeKitchenNote(item.notes)}
                      extraLine={
                        removedDeltaPrep2 > 0 ? (
                          <span className={k.deltaHint}>
                            -{removedDeltaPrep2} REMOVED (was {initialQty})
                          </span>
                        ) : null
                      }
                    />
                    <button
                      type="button"
                      disabled={!canToggle}
                      onClick={() => {
                        if (!canToggle) return;
                        const newCompleted = isCompleted ? 0 : initialQty;
                        onUpdateOrderItemStatus(order.id, originalIndex, newCompleted >= item.quantity ? 'completed' : 'pending', newCompleted);
                      }}
                      className={`${k.doneBtn} ${doneBtnChit} ${
                        canToggle
                          ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-emerald-600 text-white hover:bg-emerald-700')
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isCompleted ? 'Undo' : 'Done'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Show item list without buttons for awaiting service orders */}
      {order.status === 'ready' && allItemsCompleted && !allItemsServed && (
        <div className={listSectionClass}>
          {isRunningOrderTicket ? (
            <ul className={fitListUl}>
              {(visibleBatchInfo ? visibleBatchInfo.batchItems.length : deltaDisplayItems.length) > 0 ? (
                (visibleBatchInfo
                  ? visibleBatchInfo.batchItems.map(({ item, originalIndex, displayQty }) => ({
                      item,
                      originalIndex,
                      displayQty,
                      isNew: false,
                    }))
                  : deltaDisplayItems
                ).map(({ item, originalIndex, displayQty, isNew }) => {
                  const fullItem = order.items?.[originalIndex] ?? item;
                  const batchIdx = typeof visibleBatchIndex === 'number' ? visibleBatchIndex : 1;
                  const effectiveCompleted = getEffectiveQuantityCompletedForBatch(fullItem, batchIdx, displayQty);
                  const isCompleted = effectiveCompleted >= displayQty;
                  const effectiveServed = getEffectiveQuantityServedForBatch(fullItem, batchIdx, displayQty);
                  const canToggle = effectiveServed < displayQty;
                  const lineFullyServed = effectiveServed >= displayQty;
                  return (
                    <li key={item.lineId ?? `${item.id}-${originalIndex}`} className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}>
                      <div className="flex-1 min-w-0 break-words">
                        <span className="font-semibold">{displayQty}x</span> {item.name} 
                        {isNew && <span className={`ml-1 ${k.badgeNew}`}>ADDED</span>}
                        {item.selectedMeatOption && (
                          <span className={`ml-2 ${k.meatOpt}`}>{item.selectedMeatOption}</span>
                        )}
                        {sanitizeKitchenNote(item.notes) && (
                          <div className="mt-1.5">
                            <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                              <p className={`${k.noteLabel} mb-0.5 flex items-center`}>
                                <span className="mr-1">📝</span> Special Instructions:
                              </p>
                              <p className={k.noteBody}>{sanitizeKitchenNote(item.notes)}</p>
                            </div>
                          </div>
                        )}
                        <div className="mt-1">
                          {lineFullyServed ? (
                            <span className={k.awaitingStatus}>
                              <CheckCircleIcon className="w-3 h-3" /> Served
                            </span>
                          ) : (
                            <span className={k.awaitingAmber}>
                              <ClockIcon className="w-3 h-3" /> Awaiting Service
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={!canToggle}
                        onClick={() => {
                          if (!canToggle) return;
                          const batchQty = isCompleted ? 0 : displayQty;
                          onUpdateOrderItemStatus(order.id, originalIndex, batchQty >= displayQty ? 'completed' : 'pending', batchQty, batchIdx, displayQty);
                        }}
                        className={`${k.doneBtn} ${
                          canToggle
                            ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-green-500 text-white hover:bg-green-600')
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {isCompleted ? 'Undo' : 'Done'}
                      </button>
                    </li>
                  );
                })
              ) : (
                <li className={k.emptyLine}>No items to add.</li>
              )}
            </ul>
          ) : batchesForBaseCardOnly && batchesForBaseCardOnly.some((b) => b.batchItems.length > 0) ? (
            batchesForBaseCardOnly.map((batch) => (
              <div key={`batch-${batch.batchIndex}-${batch.label}`} className={fitBatchWrap}>
                <p className={k.batchSectionLabel}>{batch.label}</p>
                <ul className={`${fitListUl} mt-1`}>
                  {batch.batchItems.map(({ item, originalIndex }) => {
                    if (item.removed) {
                      return (
                        <li key={item.lineId ?? `removed-${originalIndex}`} className={k.removedItem}>
                          <span className="font-semibold">{getRemovedItemQuantity(item)}x</span>
                          <span className="line-through">{item.name}</span>
                          <span className={k.removedTag}>REMOVED</span>
                        </li>
                      );
                    }
                    const keyForLine = item.lineId ?? `idx-${originalIndex}`;
                    const isNew = isNewByLineId.get(keyForLine) ?? false;
                    const originalQty = initialQuantityByKey.get(keyForLine);
                    const displayQtyReady = batch.batchIndex === 0 ? (baseCardTargetQtyByIndex.get(originalIndex) ?? originalQty ?? item.quantity) : item.quantity;
                    const removedDelta = originalQty != null && originalQty > item.quantity ? originalQty - item.quantity : 0;
                    const initialQty = initialQuantityByKey.get(keyForLine) ?? item.quantity;
                    const effectiveCompleted = getEffectiveQuantityCompletedForBatch(item, 0, initialQty);
                    const isCompleted = effectiveCompleted >= initialQty;
                    const effectiveServed = getEffectiveQuantityServedForBatch(item, 0, initialQty);
                    const canToggle = effectiveServed < initialQty;
                    const lineFullyServed = effectiveServed >= initialQty;
                    return (
                      <li key={item.lineId ?? `${item.id}-${originalIndex}`} className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}>
                        <div className="flex-1 min-w-0 break-words">
                          <span className="font-semibold">{displayQtyReady}x</span> {item.name} 
                          {removedDelta > 0 && (
                            <span className={k.deltaHint}>
                              -{removedDelta} REMOVED (was {originalQty})
                            </span>
                          )}
                          {isNew && <span className={`ml-1 ${k.badgeNew}`}>ADDED</span>}
                          {item.selectedMeatOption && (
                            <span className={`ml-2 ${k.meatOpt}`}>{item.selectedMeatOption}</span>
                          )}
                          {sanitizeKitchenNote(item.notes) && (
                            <div className="mt-1.5">
                              <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                                <p className={`${k.noteLabel} mb-0.5 flex items-center`}>
                                  <span className="mr-1">📝</span> Special Instructions:
                                </p>
                                <p className={k.noteBody}>{sanitizeKitchenNote(item.notes)}</p>
                              </div>
                            </div>
                          )}
                          <div className="mt-1">
                            {lineFullyServed ? (
                              <span className={k.awaitingStatus}>
                                <CheckCircleIcon className="w-3 h-3" /> Served
                              </span>
                            ) : (
                              <span className={k.awaitingAmber}>
                                <ClockIcon className="w-3 h-3" /> Awaiting Service
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={!canToggle}
                          onClick={() => {
                            if (!canToggle) return;
                            const batchQty = isCompleted ? 0 : initialQty;
                            onUpdateOrderItemStatus(order.id, originalIndex, batchQty > 0 ? 'completed' : 'pending', batchQty, 0, initialQty);
                          }}
                          className={`${k.doneBtn} ${
                            canToggle
                              ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-green-500 text-white hover:bg-green-600')
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isCompleted ? 'Undo' : 'Done'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          ) : (
            <ul className={fitListUl}>
              {removedItems.length > 0 && removedItems.map((entry) => (
                <li key={`removed-${entry.lineId}`} className={k.removedItem}>
                  <span className="font-semibold">{entry.quantity}x</span>
                  <span className="line-through">{entry.name}</span>
                          <span className={k.removedTag}>REMOVED</span>
                </li>
              ))}
              {filteredItems.map((item, index) => {
                const originalIndex = order.items.findIndex(i => (i.lineId ? i.lineId === item.lineId : i.id === item.id));
                if (originalIndex === -1) return null;
                if (item.removed) {
                  return (
                    <li key={item.lineId ?? `removed-${originalIndex}`} className={k.removedItem}>
                      <span className="font-semibold">{getRemovedItemQuantity(item)}x</span>
                      <span className="line-through">{item.name}</span>
                      <span className={k.removedTag}>REMOVED</span>
                    </li>
                  );
                }
                const keyForLine = item.lineId ?? `idx-${originalIndex}`;
                const isNew = isNewByLineId.get(keyForLine) ?? false;
                const originalQtyFallback = initialQuantityByKey.get(keyForLine);
                const removedDeltaFallback = originalQtyFallback != null && originalQtyFallback > item.quantity ? originalQtyFallback - item.quantity : 0;
                const displayQtyFallback = baseCardTargetQtyByIndex.get(originalIndex) ?? originalQtyFallback ?? item.quantity;
                const initialQty = initialQuantityByKey.get(keyForLine) ?? item.quantity;
                const effectiveCompleted = getEffectiveQuantityCompleted(item);
                const isCompleted = effectiveCompleted >= initialQty;
                const effectiveServed = getEffectiveQuantityServedForBatch(item, 0, initialQty);
                const canToggle = effectiveServed < initialQty;
                const lineFullyServed = effectiveServed >= initialQty;
                return (
                  <li key={item.lineId ?? `${item.id}-${originalIndex}`} className={`${k.itemRow} flex items-start justify-between gap-3 rounded-md ${fitCellPad} ${itemRowTile} ${isCompleted ? 'text-gray-400 line-through opacity-75' : 'text-gray-700'}`}>
                        <div className="flex-1 min-w-0 break-words">
                          <span className="font-semibold">{displayQtyFallback}x</span> {item.name} 
                          {removedDeltaFallback > 0 && (
                            <span className={k.deltaHint}>
                              -{removedDeltaFallback} REMOVED (was {originalQtyFallback})
                            </span>
                          )}
                      {isNew && <span className={`ml-1 ${k.badgeNew}`}>ADDED</span>}
                      {item.selectedMeatOption && (
                        <span className={`ml-2 ${k.meatOpt}`}>{item.selectedMeatOption}</span>
                      )}
                      {sanitizeKitchenNote(item.notes) && (
                        <div className="mt-1.5">
                          <div className="bg-yellow-100 border-2 border-yellow-400 rounded-lg px-2 py-1.5 shadow-sm ring-1 ring-amber-600/30">
                            <p className={`${k.noteLabel} mb-0.5 flex items-center`}>
                              <span className="mr-1">📝</span> Special Instructions:
                            </p>
                            <p className={k.noteBody}>{sanitizeKitchenNote(item.notes)}</p>
                          </div>
                        </div>
                      )}
                      <div className="mt-1">
                        {lineFullyServed ? (
                          <span className={k.awaitingStatus}>
                            <CheckCircleIcon className="w-3 h-3" /> Served
                          </span>
                        ) : (
                          <span className={k.awaitingAmber}>
                            <ClockIcon className="w-3 h-3" /> Awaiting Service
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={!canToggle}
                      onClick={() => {
                        if (!canToggle) return;
                        const newCompleted = isCompleted ? 0 : initialQty;
                        onUpdateOrderItemStatus(order.id, originalIndex, newCompleted >= item.quantity ? 'completed' : 'pending', newCompleted);
                      }}
                      className={`${k.doneBtn} ${
                        canToggle
                          ? (isCompleted ? 'bg-gray-200 text-gray-500 hover:bg-gray-300' : 'bg-green-500 text-white hover:bg-green-600')
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isCompleted ? 'Undo' : 'Done'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {actionableOrderNote && (
        <div
          className={
            fitToCell
              ? 'mt-1.5 shrink-0 rounded-md border-2 border-amber-600 bg-gradient-to-b from-amber-100 to-amber-50 px-2 py-2 shadow-[0_3px_12px_rgba(217,119,6,0.38)] ring-2 ring-amber-400/50'
              : 'mt-2 pt-2 border-t border-yellow-200 bg-yellow-50 rounded p-2 ring-1 ring-amber-600/25'
          }
        >
          <p className={`${k.orderNotesTitle} ${fitToCell ? 'flex items-center gap-1' : ''}`}>
            {fitToCell && <span aria-hidden>📝</span>}
            {fitToCell ? 'Order notes' : 'Order Notes:'}
          </p>
          <p className={k.orderNotesBody}>{actionableOrderNote}</p>
        </div>
      )}
      {onRemoveOrder && (
        <button
          onClick={async () => {
            if (confirm('Remove this order from kitchen display?')) {
              await onRemoveOrder(order.id);
            }
          }}
          className={k.removeBtn}
        >
          <XCircleIcon className="w-4 h-4" />
          Remove Order
        </button>
      )}
        </div>
      </div>
    </div>
  );
};

export default KitchenOrderCard;

